"use client"

import { cn } from '@/shared/utils/tw'
import Image from 'next/image'
import React, { memo } from 'react'
import { useRouter } from 'next/navigation'
import { ModalProvider } from '@/shared/providers/ModalProvider'
import NavigationMenu from './navigation-menu'
import LanguageSelect from '@/features/select-language/ui/language-select'
import { LlmModelSelect } from '@/features/select-llm-model'
import { useNavigateChatMode } from '@/layouts/avatar-page/model/use-navigate-chat-mode'

interface NavigationBarProps {
  title?: string
  // 배경 밝기에 따른 스타일 (밝은 배경 → 어두운 글자)
  variant?: 'dark' | 'light'
}

const NavigationBar = ({ title, variant = 'dark' }: NavigationBarProps = {}) => {
  const isLight = variant === 'light'
  return (
    <ModalProvider>
      <nav
        className={cn("w-full flex items-center justify-between absolute top-0 left-0 z-[100] pointer-events-auto")}
        style={{
          height: '48px',
          paddingLeft: '8px',
          paddingRight: '12px',
          marginTop: '8px',
        }}
      >
        <div className="flex items-center shrink-0" style={{ width: '100px' }}>
          <Logo variant={variant} />
        </div>
        {title && (
          <div className="flex-1 flex items-center justify-center" style={{ gap: '8px' }}>
            <span
              className="flex items-center justify-center font-pretendard font-semibold text-[#bb99b4] whitespace-nowrap"
              style={{
                fontSize: '12px',
                lineHeight: '1.4',
                background: 'white',
                backdropFilter: 'blur(7.5px)',
                borderRadius: '5px',
                padding: '2px 4px',
                border: isLight ? '1px solid rgba(0,0,0,0.08)' : undefined,
              }}
            >
              시범 운영
            </span>
            <span
              className={cn(
                "font-pretendard font-semibold text-center whitespace-nowrap",
                isLight ? "text-[#222222]" : "text-white"
              )}
              style={{ fontSize: '18px', lineHeight: '1.3' }}
            >
              {title}
            </span>
          </div>
        )}
        <div className="flex items-center justify-end shrink-0" style={{ minWidth: '100px', gap: '8px' }}>
          <LlmModelSelect variant={variant} />
          <LanguageSelect variant={variant} />
        </div>
        {/* <Hamburger /> */}
      </nav>
      <NavigationMenu />
    </ModalProvider>
  )
}

const BackButton = () => {
  const router = useRouter();
  return (
    <button
      onClick={() => router.back()}
      className={cn(
        "w-[36px] h-[36px] rounded-full",
        "bg-white/20 hover:bg-white/30",
        "flex items-center justify-center",
        "transition-colors duration-200",
        "backdrop-blur-sm"
      )}
      aria-label="대시보드로 돌아가기"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-white"
      >
        <path d="M19 12H5M12 19l-7-7 7-7" />
      </svg>
    </button>
  )
}

const Logo = ({ variant = 'dark' }: { variant?: 'dark' | 'light' }) => {
  const { navigateToMode } = useNavigateChatMode();
  const isLight = variant === 'light'
  return (
    <div className='flex items-center cursor-pointer' style={{ gap: '4px' }} onClick={() => navigateToMode(null)}>
      <svg className={isLight ? 'text-[#222222]' : 'text-white'} width="24" height="24" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
        <path d="M16 4L8.70711 11.2929C8.31658 11.6834 8.31658 12.3166 8.70711 12.7071L16 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <Image
        width={32}
        height={22}
        src={"/svg/gg-logo.svg"}
        alt="Logo"
        draggable={false}
        className={isLight ? 'invert' : undefined}
      />
    </div>
  )
}

// const Hamburger = () => {
//   const { isModalOpen, toggleModal } = useModal()
//   // TODO: 햄버거가 열리면 음소거, 햄버거가 close면 마이크 오픈
//   const topLineRef = useRef<HTMLSpanElement>(null)
//   const bottomLineRef = useRef<HTMLSpanElement>(null)
//   const timelineRef = useRef<gsap.core.Timeline | null>(null)

//   useEffect(() => {
//     // GSAP Timeline 초기화
//     timelineRef.current = gsap.timeline({ paused: true })

//     // 초기 상태 설정
//     gsap.set([topLineRef.current, bottomLineRef.current], {
//       transformOrigin: "center"
//     })

//     // 애니메이션 시퀀스 정의
//     timelineRef.current
//       // 1단계: 선들이 중앙으로 합쳐짐
//       .to(topLineRef.current, {
//         y: 5.5, // 위 선을 아래로 (1.5px에서 7px로)
//         duration: 0.2,
//         ease: "power1.linear"
//       }, 0.2)
//       .to(bottomLineRef.current, {
//         y: -5.5, // 아래 선을 위로 (12px에서 7px로)
//         duration: 0.2,
//         ease: "power1.linear"
//       }, 0.2)
//       // 2단계: 십자가(+) 만들기
//       .to(topLineRef.current, {
//         rotation: 0, // 가로 선
//         duration: 0.1,
//         ease: "power1.linear"
//       }, 0.4)
//       .to(bottomLineRef.current, {
//         rotation: 90, // 세로 선
//         duration: 0.1,
//         ease: "power1.linear"
//       }, 0.4)
//       // // 3단계: 여러 바퀴 돌면서 X 표시로 변환
//       .to(topLineRef.current, {
//         rotation: 45, // 2바퀴 + 45도
//         duration: 0.2,
//         ease: "power1.linear"
//       }, 0.5)
//       .to(bottomLineRef.current, {
//         rotation: 135, // 2바퀴 - 45도 (90도에서 시작이므로 실제로는 90 + 720 - 45 = 765도)
//         duration: 0.2,
//         ease: "power1.linear"
//       }, 0.5)

//     return () => {
//       if (timelineRef.current) {
//         timelineRef.current.kill()
//       }
//     }
//   }, [])

//   const toggleMenu = () => {
//     if (!isModalOpen) {
//       // 메뉴 열기
//       timelineRef.current?.play()
//       toggleModal()
//     } else {
//       // 메뉴 닫기
//       timelineRef.current?.reverse()
//       toggleModal()
//     }
//   }

//   return (
//     <div
//       className='w-[63px] h-[63px] flex justify-center items-center cursor-pointer'
//       onClick={toggleMenu}
//     >
//       <div className="relative w-[28px] h-[18px] translate-y-[-5px]">
//         <span
//           ref={topLineRef}
//           className="absolute left-0 top-[1.5px] w-full h-[3px] bg-[#F0F0F0] rounded-full"
//         />
//         <span
//           ref={bottomLineRef}
//           className="absolute left-0 top-[14px] w-full h-[3px] bg-[#F0F0F0] rounded-full"
//         />
//       </div>
//     </div>
//   )
// }

export default memo(NavigationBar)